(() => {
  const elementName = 'collection-infinite-scroll';
  const automaticRetryDelays = [500, 1200];

  if (customElements.get(elementName)) return;

  class CollectionInfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.active = false;
      this.loading = false;
      this.observer = null;
      this.abortController = null;
      this.requestVersion = 0;
      this.handleFallbackClick = this.handleFallbackClick.bind(this);
    }

    connectedCallback() {
      this.active = true;
      this.loading = false;
      this.nextUrl = this.dataset.nextUrl || '';
      this.sectionId = this.dataset.sectionId || '';
      this.gridContainer = this.closest('#ProductGridContainer');
      this.grid = this.gridContainer?.querySelector('#product-grid');
      this.statusElement = this.querySelector('[data-infinite-scroll-status]');
      this.fallbackLink = this.querySelector('[data-infinite-scroll-fallback]');
      this.defaultFallbackText = this.fallbackLink?.textContent.trim() || '';
      this.fallbackLink?.addEventListener('click', this.handleFallbackClick);
      this.updateFallbackLink();

      if (!this.canEnhance()) {
        this.showFallback();
        return;
      }

      this.classList.add('is-enhanced');
      this.setState('idle');
      this.observer = new window.IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNextPage('auto_load');
          }
        },
        {
          rootMargin: '0px 0px 600px 0px',
          threshold: 0,
        },
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.active = false;
      this.loading = false;
      this.requestVersion += 1;
      this.observer?.disconnect();
      this.abortController?.abort();
      this.grid?.removeAttribute('aria-busy');
      this.fallbackLink?.removeEventListener('click', this.handleFallbackClick);
      this.observer = null;
      this.abortController = null;
    }

    canEnhance() {
      return Boolean(
        this.nextUrl &&
          this.sectionId &&
          this.grid &&
          typeof window.fetch === 'function' &&
          typeof window.DOMParser === 'function' &&
          typeof window.IntersectionObserver === 'function',
      );
    }

    buildRequestUrl(nextUrl) {
      const requestUrl = this.buildPageUrl(nextUrl);
      requestUrl.searchParams.set('section_id', this.sectionId);
      return requestUrl.toString();
    }

    buildPageUrl(nextUrl) {
      const pageUrl = new URL(nextUrl, window.location.origin);
      const currentUrl = new URL(window.location.href || window.location.origin);
      const currentParameterNames = new Set(currentUrl.searchParams.keys());

      currentParameterNames.forEach((name) => {
        if (name === 'page' || name === 'section_id') return;

        pageUrl.searchParams.delete(name);
        currentUrl.searchParams.getAll(name).forEach((value) => {
          pageUrl.searchParams.append(name, value);
        });
      });

      return pageUrl;
    }

    trackLoadResult(interactionName, interactionResult) {
      window.TorrasAnalytics?.trackInteraction(this, {
        componentId: 'collection_infinite_scroll',
        elementType: interactionName === 'load_more' ? 'button' : 'component',
        elementId: interactionName === 'load_more' ? 'load_more' : 'infinite_scroll',
        interactionName,
        interactionResult,
      });
    }

    async loadNextPage(interactionName = 'auto_load') {
      if (!this.active || this.loading || !this.nextUrl) return;

      this.loading = true;
      this.observer?.unobserve(this);
      this.setState('loading');

      const requestVersion = ++this.requestVersion;
      this.abortController =
        typeof window.AbortController === 'function' ? new window.AbortController() : null;

      try {
        const response = await this.fetchWithRetry(
          this.buildRequestUrl(this.nextUrl),
          requestVersion,
        );

        const responseHtml = await response.text();

        if (!this.isCurrentRequest(requestVersion)) return;

        const responseDocument = new window.DOMParser().parseFromString(responseHtml, 'text/html');
        const responseGrid = responseDocument.querySelector('#ProductGridContainer #product-grid');
        const productItems = responseGrid?.querySelectorAll(':scope > li.grid__item');

        if (!productItems?.length) {
          throw new Error('The next collection page did not contain product cards');
        }

        const fragment = document.createDocumentFragment();
        productItems.forEach((item) => {
          fragment.appendChild(document.importNode(item, true));
        });

        if (!this.isCurrentRequest(requestVersion)) return;

        this.grid.appendChild(fragment);

        const nextComponent = responseDocument.querySelector(
          'collection-infinite-scroll[data-next-url]',
        );
        this.nextUrl = nextComponent?.dataset.nextUrl || '';
        this.dataset.nextUrl = this.nextUrl;
        this.trackLoadResult(interactionName, 'success');

        if (this.nextUrl) {
          this.updateFallbackLink();
          this.setState('loaded');
          window.setTimeout(() => {
            if (!this.isCurrentRequest(requestVersion) || !this.nextUrl) return;
            this.setState('idle');
            this.observer?.observe(this);
          }, 600);
        } else {
          this.setState('completed');
          this.observer?.disconnect();
        }
      } catch (error) {
        if (error?.name === 'AbortError' || !this.isCurrentRequest(requestVersion)) return;

        this.trackLoadResult(interactionName, 'error');
        this.setState('error');
      } finally {
        if (this.requestVersion === requestVersion) {
          this.loading = false;
          this.abortController = null;
          this.grid?.removeAttribute('aria-busy');
        }
      }
    }

    async fetchWithRetry(requestUrl, requestVersion) {
      let lastError = new Error('Unable to load the next collection page');

      for (let attempt = 0; attempt <= automaticRetryDelays.length; attempt += 1) {
        let response;

        try {
          response = await window.fetch(requestUrl, {
            headers: {
              Accept: 'text/html',
              'X-Requested-With': 'XMLHttpRequest',
            },
            signal: this.abortController?.signal,
          });
        } catch (error) {
          if (error?.name === 'AbortError') throw error;
          lastError = error;
        }

        if (response?.ok) return response;

        if (response) {
          lastError = new Error(`Unable to load collection page (${response.status})`);
          if (!this.isRetryableStatus(response.status)) throw lastError;
        }

        if (attempt === automaticRetryDelays.length) throw lastError;

        await this.waitForRetry(automaticRetryDelays[attempt], requestVersion);
      }

      throw lastError;
    }

    isRetryableStatus(status) {
      return status === 408 || status === 429 || status >= 500;
    }

    async waitForRetry(delay, requestVersion) {
      await new Promise((resolve) => window.setTimeout(resolve, delay));

      if (!this.isCurrentRequest(requestVersion)) {
        const abortError = new Error('Collection request is no longer active');
        abortError.name = 'AbortError';
        throw abortError;
      }
    }

    handleFallbackClick(event) {
      if (!this.classList.contains('is-error') || !this.canEnhance()) return;

      event.preventDefault();
      return this.loadNextPage('load_more');
    }

    isCurrentRequest(requestVersion) {
      return this.active && this.isConnected && this.requestVersion === requestVersion;
    }

    setState(state) {
      ['idle', 'loading', 'loaded', 'error', 'completed'].forEach((name) => {
        this.classList.remove(`is-${name}`);
      });
      this.classList.add(`is-${state}`);

      if (state === 'loading') {
        this.grid?.setAttribute('aria-busy', 'true');
      } else {
        this.grid?.removeAttribute('aria-busy');
      }

      const textByState = {
        loading: this.dataset.loadingText,
        loaded: this.dataset.loadedText,
        error: this.dataset.retryText,
        completed: this.dataset.completeText,
      };
      const statusText = textByState[state];

      if (statusText && this.statusElement) {
        this.statusElement.textContent = statusText;
      }

      if (this.fallbackLink) {
        this.fallbackLink.textContent =
          state === 'error'
            ? this.dataset.retryButtonText || this.defaultFallbackText
            : this.defaultFallbackText;
      }
    }

    updateFallbackLink() {
      if (!this.fallbackLink || !this.nextUrl) return;

      const fallbackUrl = this.buildPageUrl(this.nextUrl);
      this.fallbackLink.setAttribute(
        'href',
        `${fallbackUrl.pathname}${fallbackUrl.search}${fallbackUrl.hash}`,
      );
    }

    showFallback() {
      this.classList.remove('is-enhanced');
      this.observer?.disconnect();
    }
  }

  customElements.define(elementName, CollectionInfiniteScroll);
})();
